import React, { createContext, useCallback, useRef, useState } from "react";

export type AdditionsTourId = "create-additions" | "attach-additions";

export interface AdditionsTourContextType {
    activeTour: AdditionsTourId | null;
    tourStep: number;
    isTourOpen: boolean;

    // Refs – tour 1: Create additions
    additionsTabRef: React.RefObject<HTMLElement>;
    addGroupBtnRef: React.RefObject<HTMLButtonElement>;
    addAdditionBtnRef: React.RefObject<HTMLButtonElement>;

    // Refs – tour 2: Attach additions to product
    editFirstProductRef: React.RefObject<HTMLElement>;
    firstExtensionCategoryRef: React.RefObject<HTMLElement>;

    // Actions
    startTour: (tourId: AdditionsTourId) => void;
    setTourStep: (step: number) => void;
    advanceStep: (fromStep: number) => void;
    closeTour: () => void;
}

export const AdditionsTourContext = createContext<AdditionsTourContextType | undefined>(undefined);

export const AdditionsTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeTour, setActiveTour] = useState<AdditionsTourId | null>(null);
    const [tourStep, setTourStep] = useState(0);
    const [isTourOpen, setIsTourOpen] = useState(false);

    const additionsTabRef = useRef<HTMLElement>(null);
    const addGroupBtnRef = useRef<HTMLButtonElement>(null);
    const addAdditionBtnRef = useRef<HTMLButtonElement>(null);
    const editFirstProductRef = useRef<HTMLElement>(null);
    const firstExtensionCategoryRef = useRef<HTMLElement>(null);

    const startTour = useCallback((tourId: AdditionsTourId) => {
        setActiveTour(tourId);
        setTourStep(0);
        setIsTourOpen(true);
    }, []);

    const advanceStep = useCallback((fromStep: number) => {
        setTourStep((current) => {
            if (current === fromStep) return fromStep + 1;
            return current;
        });
    }, []);

    const closeTour = useCallback(() => {
        setIsTourOpen(false);
        setActiveTour(null);
        setTourStep(0);
    }, []);

    return (
        <AdditionsTourContext.Provider
            value={{
                activeTour,
                tourStep,
                isTourOpen,
                additionsTabRef,
                addGroupBtnRef,
                addAdditionBtnRef,
                editFirstProductRef,
                firstExtensionCategoryRef,
                startTour,
                setTourStep,
                advanceStep,
                closeTour,
            }}
        >
            {children}
        </AdditionsTourContext.Provider>
    );
};
